import React, { CSSProperties, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Button,
  Container,
  Popover,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { PropertyFilterInput } from "./PropertyFilterInput";
import {
  SavedFilterSetsSelect,
  FilterActionsDropdown,
} from "./SavedFilterDropdown";
import { RootState } from "../app/rootReducer";
import {
  PropertyFilterGroup,
  clearAppliedQuery,
  hasAnyFilter,
  loadFromUrl,
  setActivePropertyFiltersVisibilityEnabled,
  setQueryMode,
  setQueryString,
  sumPropertyFilterQueries,
} from "../searchSideBar/searchSideBarConnector";
import { clearNotifications } from "../notification/notificationConnector";
import { resetStatusOverviewAggregation } from "../validation/statusOverview/statusOverviewAggregationConnector";
import { getFilterWithAggregatedData } from "../searchSideBar/filterWithAggregatedDataConnector";
import { getProgramNames } from "../searchSideBar/upstreamAttributesConnector";
import { getProductLines } from "../searchSideBar/productLinesConnector";
import { setAsinPageNumber } from "../porAsinPageControl/porAsinPageControl";
import { setValidationPageNumber } from "../validation/asinValidationTableConnector";
import { setBaselinePageNumber } from "../dateBaseline/baselineTableConnector";
import { trackButtonClick } from "../telemetry/telemetry";
import { getAmazonTinyUrl } from "../app/api";
import { CopyToClipboard } from "../../util/browserUtils";
import { useScrollDismissKey } from "../../util/useScrollDismissKey";

// Input grows to fill the row so the property filter uses the full
// canvas; Submit sits at the row's right end. `minWidth: 0` lets the
// polaris chip row wrap instead of overflowing.
const ROW_STYLE: CSSProperties = {
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const INPUT_STYLE: CSSProperties = { flex: 1, minWidth: 0 };

/**
 * App Shell Filter Bar. On mount dispatches the same data loads as
 * Legacy `SearchSideBar` so dropdowns populate, and forces
 * `queryMode = "PropertyFilter"` so Submit reads the right slice
 * field. Submit resets per-view page counters (LEAP-7748).
 */
export const FilterBar: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { key: sharePopoverKey, arm: armSharePopover } = useScrollDismissKey();
  const { queryMode, pendingQueryString, queryPropertyFilters, queryString } =
    useSelector((state: RootState) => state.generateQuery);
  const { isEditing } = useSelector(
    (state: RootState) => state.asinBaselineTableControl
  );
  const { filterWithAggregatedData } = useSelector(
    (state: RootState) => state.filterWithAggregatedData
  );

  const canSubmit = useMemo(
    () => hasAnyFilter(queryPropertyFilters),
    [queryPropertyFilters]
  );

  // Dirty tracking: enable Submit only when the current chip state
  // would produce a different query string than the one currently
  // applied. Programmatic drill-downs from Status Overview segments
  // and Step Breakdown cells already dispatch `setQueryString`, so
  // their state changes land in `queryString` at the same time as
  // the chip changes — the pending vs. applied strings match, and
  // Submit stays disabled. Only manual chip edits leave the two out
  // of sync and re-enable Submit.
  const pendingQueryStringFromFilters = useMemo(() => {
    try {
      return sumPropertyFilterQueries(queryPropertyFilters).toString();
    } catch {
      // Defensively enable Submit if we can't serialize (e.g., a
      // partially-typed chip). Better to over-enable than to
      // strand the user with a disabled Submit they can't fix.
      return null;
    }
  }, [queryPropertyFilters]);

  const isDirty =
    pendingQueryStringFromFilters === null ||
    pendingQueryStringFromFilters !== queryString;

  // On nav, re-sync chips with the URL hash. Skip when no hash so
  // we don't clobber a cleared query with `(all)`.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      dispatch(loadFromUrl());
    }
  }, [location.pathname]);

  // Wire the browser Back/Forward buttons to Redux. Submits push a
  // new history entry via `pushState`, which does NOT fire hashchange
  // or a react-router route change (pathname is unchanged), so the
  // usual `location.pathname` effect above never sees the pop. Listen
  // for `popstate` directly and re-hydrate from the URL so chips and
  // the applied query snap back to the previous filter.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      dispatch(loadFromUrl());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Guard: PorAsinListTable's `loadFromUrl()` can flip mode to
  // `UIFilter`, silently breaking Submit. Snap back.
  useEffect(() => {
    if (queryMode !== "PropertyFilter") {
      dispatch(setQueryMode("PropertyFilter"));
    }
  }, [queryMode]);

  useEffect(() => {
    dispatch(getFilterWithAggregatedData());
    dispatch(setActivePropertyFiltersVisibilityEnabled(true));
  }, []);

  useEffect(() => {
    if (filterWithAggregatedData?.status === "finished") {
      dispatch(getProgramNames());
      dispatch(getProductLines());
    }
  }, [filterWithAggregatedData]);

  // On Clear filters (canSubmit true -> false): wipe queryString +
  // URL hash so tables skip fetching until the next Submit, dismiss
  // stale flashbar notifications, and drop the status-overview
  // aggregation state so the Stale Validation Data alert (driven by
  // the last aggregation's per-service earliest run timestamps)
  // disappears too.
  const prevCanSubmitRef = useRef(canSubmit);
  useEffect(() => {
    if (prevCanSubmitRef.current && !canSubmit) {
      dispatch(clearAppliedQuery());
      dispatch(clearNotifications());
      dispatch(resetStatusOverviewAggregation());
    }
    prevCanSubmitRef.current = canSubmit;
  }, [canSubmit]);

  const onSubmit = () => {
    trackButtonClick("filterBar", "Submit", {
      queryMode,
      query: pendingQueryString,
    });
    // Guarantee PropertyFilter mode in case of route-change drift.
    dispatch(setQueryMode("PropertyFilter"));
    dispatch(setQueryString());
    // LEAP-7748: reset page numbers on fresh search.
    dispatch(setAsinPageNumber({ pageNumber: 1 }));
    dispatch(setValidationPageNumber({ pageNumber: 1 }));
    dispatch(setBaselinePageNumber({ pageNumber: 1 }));
  };

  // Shorten the current URL (which encodes the applied filter in its
  // hash) via Amazon TinyURL and copy the result to the clipboard.
  // Falls back to the raw URL if the TinyURL service is unreachable.
  const onShare = async () => {
    trackButtonClick("filterBar", "Share", { query: pendingQueryString });
    const currentUrl = window.location.href;
    const tinyUrl = await getAmazonTinyUrl(currentUrl);
    CopyToClipboard(tinyUrl);
  };

  return (
    <Container>
      <div data-testid="filter-bar" style={ROW_STYLE}>
        <div style={INPUT_STYLE}>
          <PropertyFilterInput
            enableFreeTextAsin
            tokenLimit={5}
            customControl={<SavedFilterSetsSelect />}
            customFilterActions={
              <FilterActionsDropdown
                canSave={canSubmit}
                onClear={() => {
                  dispatch(clearAppliedQuery());
                  // Drop notifications tied to the previous applied
                  // filter (Restricted ASINs, error alerts, etc.) so
                  // they don't linger over the empty state. The
                  // canSubmit-false effect will fire once the chip
                  // wipe below propagates, but pairing the clear with
                  // the immediate URL/queryString reset here avoids
                  // any window where a stale alert survives.
                  dispatch(clearNotifications());
                  dispatch(resetStatusOverviewAggregation());
                }}
                clearDisabled={!canSubmit}
              />
            }
          />
        </div>
        <Popover
          key={sharePopoverKey}
          size="small"
          // `bottom` + portal: opens the "Link copied" bubble below
          // the share button. `renderWithPortal` moves the panel to
          // Cloudscape's overlay layer so it isn't trapped inside
          // the FilterBar's stacking context or clipped by the
          // header row above.
          //
          // `sharePopoverKey` from `useScrollDismissKey` remounts the
          // popover on the first scroll after a click, closing it
          // before the (position-fixed) panel drifts out of place.
          position="bottom"
          triggerType="custom"
          dismissButton={false}
          renderWithPortal
          content={
            <StatusIndicator type="success">Link copied</StatusIndicator>
          }
        >
          <Button
            data-testid="filter-bar-share-button"
            iconName="share"
            ariaLabel="Copy shareable link"
            variant="icon"
            onClick={() => {
              armSharePopover();
              onShare();
            }}
          />
        </Popover>
        <Button
          data-testid="filter-bar-submit-button"
          variant="primary"
          disabled={isEditing || !canSubmit || !isDirty}
          onClick={onSubmit}
        >
          Submit
        </Button>
      </div>
    </Container>
  );
};

export default FilterBar;
